import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Film, AlertCircle, CheckCircle2, Loader2, X, UploadCloud, Play, Pause } from 'lucide-react';
import Hls from 'hls.js';
import { api } from '../../../api/client';
import { Can } from '../../../lib/rbac';

interface ClipCandidate {
  id: number;
  asin: string;
  cbt_item_id: string | null;
  source: string;
  video_url: string;
  video_image_url: string | null;
  duration_sec: number | null;
  creator: string | null;
  creator_name: string | null;
  title: string | null;
  is_vertical: boolean | null;
  status: string;
  fetched_at: string;
  ml_clip_uuid: string | null;
  ml_uploaded_at: string | null;
  processed_video_url: string | null;
}

interface LookupResponse {
  success: boolean;
  asin?: string;
  cbtItemId?: string | null;
  tokensLeft?: number | null;
  videosFound?: number;
  inserted?: number;
  updated?: number;
  rows?: ClipCandidate[];
  error?: string;
}

interface ListResponse {
  success: boolean;
  count: number;
  rows: ClipCandidate[];
  error?: string;
}

const STATUS_COLORS: Record<string, string> = {
  available: 'text-[var(--color-text-muted)]',
  confirmed: 'text-[var(--color-warning)]',
  processing: 'text-[var(--color-warning)]',
  uploaded: 'text-[var(--color-success)]',
  rejected_local: 'text-[var(--color-error)]',
  error: 'text-[var(--color-error)]',
};

// Aggregate status across multiple candidates of the same ASIN.
// Priority: uploaded > processing > confirmed > available > error > rejected_local
function pickAggregateStatus(vids: ClipCandidate[]): string {
  const order = ['uploaded', 'processing', 'confirmed', 'available', 'error', 'rejected_local'];
  for (const s of order) {
    if (vids.some(v => v.status === s)) return s;
  }
  return vids[0]?.status ?? 'available';
}

function eligibilityBadge(c: ClipCandidate) {
  const dur = c.duration_sec ?? 0;
  if (c.is_vertical && dur >= 10 && dur <= 61) {
    return { label: '🟢 Elegible directo', color: 'bg-green-50 text-green-700 border-green-200' };
  }
  if (c.is_vertical && dur > 61) {
    return { label: '🟡 Recortar a 60s', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
  }
  if (!c.is_vertical && dur >= 10 && dur <= 61) {
    return { label: '🟡 Crop a vertical', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
  }
  if (dur < 10) {
    return { label: '🔴 Muy corto', color: 'bg-red-50 text-red-700 border-red-200' };
  }
  return { label: '🔴 No elegible', color: 'bg-red-50 text-red-700 border-red-200' };
}

export default function ClipsTab() {
  const [candidates, setCandidates] = useState<ClipCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [lookupAsin, setLookupAsin] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMsg, setLookupMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [filterVerticalOnly, setFilterVerticalOnly] = useState(true);
  const [filterMaxDuration, setFilterMaxDuration] = useState<'61' | 'all'>('all');
  const [filterCreator, setFilterCreator] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [previewing, setPreviewing] = useState<ClipCandidate | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterVerticalOnly) params.set('isVertical', 'true');
      if (filterMaxDuration === '61') params.set('maxDuration', '61');
      if (filterCreator) params.set('creator', filterCreator);
      if (filterStatus) params.set('status', filterStatus);
      params.set('limit', '200');

      const res = await api.get<ListResponse>(`/clips/candidates?${params.toString()}`);
      if (res.success) {
        setCandidates((res as unknown as ListResponse).rows || []);
      }
    } finally {
      setLoading(false);
    }
  }, [filterVerticalOnly, filterMaxDuration, filterCreator, filterStatus]);

  useEffect(() => { reload(); }, [reload]);

  async function handleLookup() {
    const asin = lookupAsin.trim().toUpperCase();
    if (!/^[A-Z0-9]{10}$/.test(asin)) {
      setLookupMsg({ kind: 'err', text: 'ASIN debe tener 10 caracteres alfanuméricos' });
      return;
    }
    setLookupBusy(true);
    setLookupMsg(null);
    try {
      const res = await api.post<LookupResponse>('/clips/lookup-asin', { asin });
      const r = res as unknown as LookupResponse;
      if (r.success) {
        setLookupMsg({
          kind: 'ok',
          text: `${asin}: ${r.videosFound} video(s) encontrados (${r.inserted} nuevos, ${r.updated} actualizados). Tokens Keepa: ${r.tokensLeft}`,
        });
        setLookupAsin('');
        await reload();
      } else {
        setLookupMsg({ kind: 'err', text: `${asin}: ${r.error || 'error desconocido'}` });
      }
    } catch (err) {
      setLookupMsg({ kind: 'err', text: err instanceof Error ? err.message : 'network error' });
    } finally {
      setLookupBusy(false);
    }
  }

  async function handleReject(id: number) {
    await api.post(`/clips/${id}/reject`, {});
    await reload();
  }

  // Group candidates by ASIN for the table view
  const byAsin = new Map<string, ClipCandidate[]>();
  for (const c of candidates) {
    if (!byAsin.has(c.asin)) byAsin.set(c.asin, []);
    byAsin.get(c.asin)!.push(c);
  }

  return (
    <div className="space-y-4">
      {/* Header & manual ASIN input */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Film size={20} className="text-[var(--color-primary)]" />
          <h2 className="text-lg font-semibold">Clips para ML CBT</h2>
        </div>

        <Can cap="catalog.edit_listing">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
            <input
              type="text"
              value={lookupAsin}
              onChange={e => setLookupAsin(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
              placeholder="Pegá un ASIN (ej: B0DQVTV75D) y enter"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm focus:outline-none focus:border-[var(--color-primary)]"
              disabled={lookupBusy}
            />
          </div>
          <button
            onClick={handleLookup}
            disabled={lookupBusy || !lookupAsin}
            className="px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {lookupBusy && <Loader2 size={14} className="animate-spin" />}
            Buscar videos
          </button>
        </div>
        </Can>

        {lookupMsg && (
          <div className={`mt-2 text-xs px-3 py-2 rounded ${
            lookupMsg.kind === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          } flex items-center gap-2`}>
            {lookupMsg.kind === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {lookupMsg.text}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3">
        <span className="text-xs text-[var(--color-text-muted)] uppercase font-medium">Filtros:</span>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={filterVerticalOnly}
            onChange={e => setFilterVerticalOnly(e.target.checked)}
            className="rounded"
          />
          Solo verticales
        </label>
        <select
          value={filterMaxDuration}
          onChange={e => setFilterMaxDuration(e.target.value as 'all' | '61')}
          className="text-sm px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)]"
        >
          <option value="all">Cualquier duración</option>
          <option value="61">≤ 61s (límite ML)</option>
        </select>
        <select
          value={filterCreator}
          onChange={e => setFilterCreator(e.target.value)}
          className="text-sm px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)]"
        >
          <option value="">Cualquier creator</option>
          <option value="Main">Main (oficial)</option>
          <option value="Brand">Brand</option>
          <option value="Vendor">Vendor</option>
          <option value="Customer">Customer</option>
          <option value="Influencer">Influencer</option>
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="text-sm px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)]"
        >
          <option value="">Cualquier estado</option>
          <option value="available">Disponible</option>
          <option value="confirmed">Confirmado</option>
          <option value="processing">Procesando</option>
          <option value="uploaded">Subido</option>
          <option value="rejected_local">Descartado</option>
        </select>
        <span className="ml-auto text-xs text-[var(--color-text-muted)]">
          {candidates.length} videos · {byAsin.size} ASINs
        </span>
      </div>

      {/* Table */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-[var(--color-text-muted)]">
            <Loader2 size={20} className="animate-spin mr-2" /> Cargando...
          </div>
        ) : candidates.length === 0 ? (
          <div className="p-12 flex flex-col items-center justify-center text-[var(--color-text-muted)]">
            <Film size={32} className="mb-3 opacity-40" />
            <div className="text-sm">Aún no hay videos en el pool</div>
            <div className="text-xs mt-1">Pegá un ASIN arriba para empezar</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg)] border-b border-[var(--color-border)]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[var(--color-text-muted)]">Producto</th>
                <th className="text-left px-3 py-2 font-medium text-[var(--color-text-muted)]">Videos disponibles ({/* count info */})</th>
                <th className="text-left px-3 py-2 font-medium text-[var(--color-text-muted)]">Elegibles</th>
                <th className="text-left px-3 py-2 font-medium text-[var(--color-text-muted)]">Estado</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(byAsin.entries()).map(([asin, vids]) => {
                const eligibles = vids.filter(v => v.is_vertical && (v.duration_sec ?? 0) >= 10 && (v.duration_sec ?? 0) <= 61).length;
                const aggregateStatus = pickAggregateStatus(vids);
                const cbtId = vids.find(v => v.cbt_item_id)?.cbt_item_id;
                return (
                  <tr key={asin} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg)]">
                    <td className="px-3 py-2 align-top">
                      <div className="font-mono text-xs">{asin}</div>
                      {cbtId ? (
                        <div className="text-xs text-[var(--color-text-muted)]">{cbtId}</div>
                      ) : (
                        <div className="text-[10px] text-[var(--color-error)]">No publicado en CBT</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        {vids.map(v => {
                          const elig = eligibilityBadge(v);
                          return (
                            <button
                              key={v.id}
                              onClick={() => setPreviewing(v)}
                              className="group relative flex flex-col gap-1 text-left hover:opacity-90"
                              title={`${v.creator} — ${v.duration_sec}s${v.is_vertical ? ' vertical' : ' horizontal'}`}
                            >
                              <div className="relative">
                                {v.video_image_url ? (
                                  <img
                                    src={v.video_image_url}
                                    alt=""
                                    className="w-14 h-20 object-cover rounded border border-[var(--color-border)] group-hover:border-[var(--color-primary)]"
                                  />
                                ) : (
                                  <div className="w-14 h-20 rounded border border-[var(--color-border)] bg-[var(--color-bg)] flex items-center justify-center group-hover:border-[var(--color-primary)]">
                                    <Film size={16} className="text-[var(--color-text-muted)]" />
                                  </div>
                                )}
                                {v.processed_video_url && (
                                  <span
                                    title="Tiene video procesado (con música)"
                                    className="absolute -top-1 -right-1 bg-[var(--color-success)] text-white rounded-full w-4 h-4 flex items-center justify-center"
                                  >
                                    <CheckCircle2 size={10} />
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-[10px] text-[var(--color-text-muted)] whitespace-nowrap">
                                  {v.duration_sec}s · {v.creator?.slice(0, 8)}
                                </span>
                                <span className={`text-[9px] px-1 py-0.5 rounded border ${elig.color} text-center whitespace-nowrap leading-tight`}>
                                  {elig.label.replace(/^[🟢🟡🔴]\s/, '')}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      <div className="text-sm font-medium">{eligibles} / {vids.length}</div>
                      <div className="text-[10px] text-[var(--color-text-muted)]">vertical+≤61s</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className={`text-xs ${STATUS_COLORS[aggregateStatus] || ''}`}>{aggregateStatus}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {previewing && (
        <PreviewModal
          candidate={previewing}
          onClose={() => setPreviewing(null)}
          onConfirmed={async () => { setPreviewing(null); await reload(); }}
        />
      )}
    </div>
  );
}

// ── PreviewModal: hls.js player + confirm flow ────────────────────────────

const MUSIC_CATS = ['default', 'lego', 'libros', 'vinilos', 'consola'] as const;

function PreviewModal({
  candidate,
  onClose,
  onConfirmed,
}: {
  candidate: ClipCandidate;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [musicCat, setMusicCat] = useState<string>('');
  const [musicPlaying, setMusicPlaying] = useState(false);

  // If the clip has been processed (mute+music+trim), default to that. Toggle lets user see Keepa source.
  const hasProcessed = !!candidate.processed_video_url;
  const [showOriginal, setShowOriginal] = useState(!hasProcessed);
  const playUrl = !showOriginal && candidate.processed_video_url
    ? candidate.processed_video_url
    : candidate.video_url;

  // Bind hls.js to the <video> element
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const url = playUrl;

    if (url.endsWith('.m3u8')) {
      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(url);
        hls.attachMedia(v);
        return () => hls.destroy();
      }
      // Safari nativo
      if (v.canPlayType('application/vnd.apple.mpegurl')) {
        v.src = url;
      }
    } else {
      // MP4 directo (procesado o ya MP4 de origen)
      v.src = url;
    }
  }, [playUrl]);

  const eligible = candidate.is_vertical && (candidate.duration_sec ?? 0) >= 10;
  const willTrim = (candidate.duration_sec ?? 0) > 60;

  async function handleConfirm() {
    if (!candidate.cbt_item_id) {
      setConfirmMsg({ kind: 'err', text: 'Este ASIN no tiene CBT publicado en ML — no se puede subir.' });
      return;
    }
    setConfirming(true);
    setConfirmMsg(null);
    try {
      const body: Record<string, string> = {};
      if (musicCat) body.musicCategory = musicCat;
      const res = await api.post<{ success: boolean; clipUuid?: string; sites?: unknown[]; musicCategory?: string; error?: string }>(
        `/clips/${candidate.id}/confirm`,
        body
      );
      const r = res as unknown as { success: boolean; clipUuid?: string; musicCategory?: string; sites?: unknown[]; error?: string };
      if (r.success) {
        setConfirmMsg({
          kind: 'ok',
          text: `Subido a ML. clip_uuid=${r.clipUuid?.slice(0, 8)}... música=${r.musicCategory} sites=${r.sites?.length}. Moderación 24-48h.`,
        });
        setTimeout(onConfirmed, 1800);
      } else {
        setConfirmMsg({ kind: 'err', text: r.error || 'error' });
      }
    } catch (err) {
      setConfirmMsg({ kind: 'err', text: err instanceof Error ? err.message : 'network error' });
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] max-w-2xl w-full p-4 space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-xs text-[var(--color-text-muted)]">{candidate.asin}</div>
            <div className="font-medium text-sm">{candidate.title || '(sin título)'}</div>
            <div className="text-[11px] text-[var(--color-text-muted)]">
              {candidate.cbt_item_id ?? <span className="text-[var(--color-error)]">No publicado en CBT</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--color-text-muted)]"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <video
              ref={videoRef}
              controls
              playsInline
              poster={candidate.video_image_url ?? undefined}
              className="w-full max-h-[480px] bg-black rounded"
            />
            {hasProcessed && (
              <div className="flex items-center justify-between text-[11px]">
                <span className={showOriginal ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-success)]'}>
                  {showOriginal ? 'Original (Keepa)' : 'Procesado (con música, listo para ML)'}
                </span>
                <button
                  type="button"
                  onClick={() => setShowOriginal(s => !s)}
                  className="px-2 py-0.5 rounded border border-[var(--color-border)] hover:bg-[var(--color-bg)]"
                >
                  {showOriginal ? 'Ver procesado' : 'Ver original'}
                </button>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="text-xs space-y-1">
              <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Creator</span><span>{candidate.creator || '—'}</span></div>
              <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Nombre</span><span className="text-right">{candidate.creator_name || '—'}</span></div>
              <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Duración</span><span>{candidate.duration_sec}s {willTrim && <span className="text-[var(--color-warning)]">(se recorta a 60s)</span>}</span></div>
              <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Vertical</span><span>{candidate.is_vertical ? 'sí' : <span className="text-[var(--color-error)]">no — bloquea upload</span>}</span></div>
              <div className="flex justify-between"><span className="text-[var(--color-text-muted)]">Status</span><span>{candidate.status}</span></div>
            </div>

            <div>
              <label className="block text-xs text-[var(--color-text-muted)] mb-1">Categoría música</label>
              <div className="flex gap-2">
                <select
                  value={musicCat}
                  onChange={e => {
                    setMusicCat(e.target.value);
                    if (audioRef.current) {
                      audioRef.current.pause();
                      setMusicPlaying(false);
                    }
                  }}
                  className="flex-1 text-sm px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)]"
                >
                  <option value="">Auto (según categoría Amazon)</option>
                  {MUSIC_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const a = audioRef.current;
                    if (!a) return;
                    if (musicPlaying) {
                      a.pause();
                      setMusicPlaying(false);
                    } else {
                      // Si está en "auto" usamos default
                      const cat = musicCat || 'default';
                      a.src = `/api/clips/music/${cat}`;
                      a.play().then(() => setMusicPlaying(true)).catch(() => setMusicPlaying(false));
                    }
                  }}
                  className="px-3 py-1 rounded border border-[var(--color-border)] hover:bg-[var(--color-bg)] text-sm flex items-center gap-1"
                  title="Escuchar track"
                >
                  {musicPlaying ? <Pause size={14} /> : <Play size={14} />}
                  {musicPlaying ? 'Pausar' : 'Escuchar'}
                </button>
              </div>
              <audio ref={audioRef} onEnded={() => setMusicPlaying(false)} />
            </div>

            <Can cap="catalog.edit_listing">
            <button
              onClick={handleConfirm}
              disabled={confirming || !eligible || !candidate.cbt_item_id || candidate.status !== 'available'}
              className="w-full px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {confirming ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              {confirming ? 'Procesando...' : 'Confirmar y subir a ML'}
            </button>
            </Can>

            {!eligible && (
              <div className="text-xs text-[var(--color-error)] flex items-start gap-1">
                <AlertCircle size={12} className="mt-0.5" />
                {candidate.is_vertical ? 'Duración fuera de rango' : 'Video no es vertical'}
              </div>
            )}

            {confirmMsg && (
              <div className={`text-xs px-3 py-2 rounded flex items-start gap-2 ${
                confirmMsg.kind === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {confirmMsg.kind === 'ok' ? <CheckCircle2 size={14} className="mt-0.5" /> : <AlertCircle size={14} className="mt-0.5" />}
                <span className="break-all">{confirmMsg.text}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
